import { getDate, getDatePlusMinus, getUnixTime, trimDecimals } from '@einhaus/meteoric-helpers';
/** Calendar-day closing observations in Eastern time. Closed markets retain their last
 * valuation; every date, including dates without trades, has the same statistical weight.
 * Callers must supply a complete market-valued curve, including the reporting boundaries.
 */
export function dailyBalanceSnapshots(balances) {
    if (!balances.length)
        return [];
    const sorted = [...balances].sort((a, b) => a.timestamp - b.timestamp);
    for (const point of sorted) {
        if (!Number.isSafeInteger(point.timestamp) || !Number.isFinite(point.balance))
            throw new Error('Invalid equity observation');
    }
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const result = [];
    let date = getDate({ date: first.timestamp, format: 'ymd' });
    let index = 0;
    // A midnight starting balance is the opening boundary for the first complete day.
    if (first.timestamp === getUnixTime(`${date} 00:00:00`))
        result.push(first);
    while (true) {
        const nextDate = getDatePlusMinus('+', 1, 'day', 'ymd', null, date);
        const close = getUnixTime(`${nextDate} 00:00:00`) - 1;
        if (close > last.timestamp)
            break; // Do not give a partial last day a full day's weight.
        while (index + 1 < sorted.length && sorted[index + 1].timestamp <= close)
            index++;
        result.push({ timestamp: close, balance: sorted[index].balance });
        date = nextDate;
    }
    return result;
}
/** Daily arithmetic excess-return Sharpe. Calendar-day sampling is common to stocks,
 * options, crypto and futures; annualization is always sqrt(365.25), never trade count.
 * riskFreeRatePerPeriod/periodsPerYear describe the input yield, not sampling frequency.
 * Undefined risk (insufficient observations, zero volatility or insolvency) remains null.
 */
export function calculateSharpeRatioFromBalanceSnapshots(config) {
    const { riskFreeRatePerPeriod = 0, periodsPerYear = 252, dailyRiskFreeReturns } = config;
    if (!Number.isFinite(riskFreeRatePerPeriod) || riskFreeRatePerPeriod <= -1 || !(periodsPerYear > 0)) {
        throw new Error('Invalid risk-free return convention');
    }
    const points = dailyBalanceSnapshots(config.balances.filter((p) => config.filterStartTimestamp === undefined || p.timestamp >= config.filterStartTimestamp));
    if (dailyRiskFreeReturns && dailyRiskFreeReturns.length !== Math.max(0, points.length - 1))
        throw new Error('Daily benchmark coverage differs from equity coverage');
    if (points.length < 3 || points.some((p) => p.balance <= 0))
        return null;
    const excess = points.slice(1).map((point, i) => {
        const previous = points[i];
        const years = (point.timestamp - previous.timestamp) / (365.25 * 86400);
        const riskFree = dailyRiskFreeReturns?.[i] ?? Math.expm1(Math.log1p(riskFreeRatePerPeriod) * periodsPerYear * years);
        if (!Number.isFinite(riskFree) || riskFree <= -1)
            throw new Error('Invalid daily benchmark return');
        return point.balance / previous.balance - 1 - riskFree;
    });
    const mean = excess.reduce((sum, r) => sum + r, 0) / excess.length;
    const variance = excess.reduce((sum, r) => sum + (r - mean) ** 2, 0) / (excess.length - 1);
    if (!(variance > 0))
        return null;
    return trimDecimals((mean / Math.sqrt(variance)) * Math.sqrt(365.25));
}
//# sourceMappingURL=calculateSharpeRatioFromBalanceSnapshots.js.map